import i18next from 'i18next';
import { sha256 } from 'js-sha256';
import _ from 'lodash';
import moment from 'moment';
import { PureComponent } from 'react';

import {
    createRecordingDialogEvent,
    sendAnalytics
} from '../../../analytics';
import logger from '../../../app/logger';
import { apiKEY } from '../../../base/config/constants';
import { hideDialog } from '../../../base/dialog';
import { MHRecordingConstants } from '../../../base/lib-meet-hour';
import { ULR_PATHS } from '../../../base/mtApi/constants';
import { fetchAxiosApi } from '../../../base/mtApi/functions';
import { getLocalParticipant, recordingStartedByUser } from '../../../base/participants';
import { checkIfNotReactNative, isDomainWeborNative } from '../../../base/util/checkOS';
import {
    getDropboxData,
    getNewAccessToken as getNewDropboxAccessToken,
    isEnabled as isDropboxEnabled,
    updateDropboxToken
} from '../../../dropbox';
import { showErrorNotification } from '../../../notifications';
import {
    getOneDriveData,
    isEnabled as isOneDriveEnabled,
    updateOneDriveToken
} from '../../../onedrive';
import { toggleRequestingSubtitles } from '../../../subtitles/actions';
import {
    RECORDING_TYPES,
    isCustomS3RecordingStorage,
    isLocalRecordingStorage
} from '../../constants';
import { startRecordingSafely } from '../../functions';
import {
    markProviderValidationFailed,
    markProviderValidationSuccess,
    resetProviderSavedTokens,
    resetProviderValidation,
    tryMarkProviderTokenSaved,
    tryStartProviderValidation
} from '../providerRequestDedupe';

type Props = {

    /**
     * Requests subtitles when recording is turned on.
     */
    _autoCaptionOnRecord: boolean,

    /**
     * The {@code MHConference} for the current conference.
     */
    _conference: Object,

    /**
     * The app key for the Dropbox authentication.
     */
    _appKey: string,

    /**
     * Whether to show file recordings service, even if integrations
     * are enabled.
     */
    _fileRecordingsServiceEnabled: boolean,

    /**
     * The Dropbox refresh token.
     */
    _rToken: string,

    /**
     * Whether to show the possibility to share file recording with others
     * based on the backend implementation.
     */
    _fileRecordingsServiceSharingEnabled: boolean,

    /**
     * If true the Dropbox integration is enabled, otherwise disabled.
     */
    _isDropboxEnabled: boolean,

    /**
     * If true the OneDrive integration is enabled, otherwise disabled.
     */
    _isOneDriveEnabled: boolean,

    /**
     * The OneDrive access token.
     */
    _oneDriveToken: string,

    /**
     * The OneDrive refresh token.
     */
    _oneDriveRToken: string,

    /**
     * OneDrive access token's expiration date as UNIX timestamp.
     */
    _oneDriveTokenExpireDate?: number,

    /**
     * The app key/client ID for OneDrive authentication.
     */
    _oneDriveClientId: string,

    /**
     * OneDrive OAuth strategy.
     */
    _oneDriveAuthStrategy: string,

    /**
     * Backend endpoint used for server-side OneDrive token exchange.
     */
    _oneDriveServerTokenEndpoint: string,

    /**
     * Whether OneDrive sign-in popup + token exchange is in progress.
     */
    _oneDriveAuthorizing: boolean,

    /**
     * The Dropbox access token.
     */
    _token: string,

    /**
     * The redux dispatch function.
     */
    dispatch: Function,

    /**
     * Dropbox access token's expiration date as UNIX timestamp.
     */
    _tokenExpireDate?: number,

    /**
     * Invoked to obtain translated strings.
     */
    t: Function,
    apiN: ?Object,
    _accessToken: ?Object,
    _userId: ?string,
    _roomName: ?string,

    /**
     * The list of all participants in the conference.
     */
    _participants: Array<Object>
}

type State = {

    /**
     * <tt>true</tt> if we have a valid oauth token for the selected provider.
     */
    isTokenValid: boolean,

    /**
     * <tt>true</tt> if we are validating provider oauth token/account state.
     */
    isValidating: boolean,

    /**
     * The currently selected recording service of type: RECORDING_TYPES.
     */
    selectedRecordingService: ?string,

    /**
     * True if the user requested the service to share the recording with others.
     */
    sharingEnabled: boolean,

    /**
     * Number of MiB of available space in user's Dropbox account.
     */
    spaceLeft: ?number,

    /**
     * The display name of the user's Dropbox account.
     */
    userName: ?string,

    /**
     * The display name of the user's OneDrive account.
     */
    oneDriveUserName: ?string,

    /**
     * Number of MiB of available space in user's OneDrive account.
     */
    oneDriveSpaceLeft: ?number,

    /**
     * The email associated with the user's OneDrive account.
     */
    oneDriveUserEmail: ?string,

    /**
     * Number of MiB of total space in user's OneDrive account.
     */
    oneDriveSpaceTotal: ?number,

    /**
     * Number of MiB of used space in user's OneDrive account.
     */
    oneDriveSpaceUsed: ?number
};

/**
 * Parses storage values that may come as number or string.
 *
 * @param {number|string|null|void} value - Storage value.
 * @returns {?number}
 */
function parseStorageValue(value: number | string | null | void) {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
    }

    if (typeof value === 'string') {
        const parsed = Number.parseFloat(value.replace(/,/g, '').trim());

        return Number.isFinite(parsed) ? parsed : null;
    }

    return null;
}

/**
 * Component for the recording start dialog.
 */
class AbstractStartRecordingDialog extends PureComponent<Props, State> {
    _dropboxTokenFromRecordSettings: ?string;
    _oneDriveTokenFromRecordSettings: ?string;
    _lastTokenValidationKey: ?string;
    _inFlightTokenValidationKey: ?string;
    _lastSavedDropboxToken: ?string;
    _lastSavedOneDriveToken: ?string;

    /**
     * Initializes a new {@code StartRecordingDialog} instance.
     *
     * @inheritdoc
     */
    constructor(props: Props) {
        super(props);

        this._dropboxTokenFromRecordSettings = null;
        this._oneDriveTokenFromRecordSettings = null;
        this._lastTokenValidationKey = null;
        this._inFlightTokenValidationKey = null;
        this._lastSavedDropboxToken = null;
        this._lastSavedOneDriveToken = null;

        // Bind event handler so it is only bound once for every instance.
        this._onSubmit = this._onSubmit.bind(this);
        this._onSelectedRecordingServiceChanged = this._onSelectedRecordingServiceChanged.bind(this);
        this._onSharingSettingChanged = this._onSharingSettingChanged.bind(this);

        let selectedRecordingService;

        // TODO: Potentially check if we need to handle changes of
        // _fileRecordingsServiceEnabled and _areIntegrationsEnabled()
        if (this.props._fileRecordingsServiceEnabled || !this._areIntegrationsEnabled()) {
            selectedRecordingService = RECORDING_TYPES.MH_REC_SERVICE;
        } else if (this.props._isOneDriveEnabled) {
            selectedRecordingService = RECORDING_TYPES.ONEDRIVE;
        } else if (this._areIntegrationsEnabled()) {
            selectedRecordingService = RECORDING_TYPES.DROPBOX;
        }

        this.state = {
            isTokenValid: false,
            isValidating: false,
            userName: undefined,
            sharingEnabled: true,
            spaceLeft: undefined,
            oneDriveUserName: undefined,
            oneDriveSpaceLeft: undefined,
            oneDriveUserEmail: undefined,
            oneDriveSpaceTotal: undefined,
            oneDriveSpaceUsed: undefined,
            selectedRecordingService,
            recTypeData: {},
            isAws: false,
            isLoaded: true,
            dropLoaded: false
        };
    }

    /**
     * Validates the oauth access token.
     *
     * @inheritdoc
     * @returns {void}
     */
    componentDidMount() {
        this._fetchRecordingConfig();
    }

    /**
     * Fetches recording configuration from API and hydrates provider tokens.
     *
     * @returns {void}
     */
    _fetchRecordingConfig() {
        const { apiN: { API_URL_PREFIX, API_VERSION_PREFIX, MH_CLIENT_ID } } = this.props;
        const moement = moment().seconds(0)
        .milliseconds(0)
        .format('X');

        const mesh256 = sha256(`${apiKEY}:MH:${moement}`);

        fetchAxiosApi(ULR_PATHS.AUTO_REC_STREAM, {
            method: 'post',
            headers: { 'Content-Type': 'application/json' },
            body: {
                client_id: MH_CLIENT_ID,
                meeting_id: this.props._roomName,
                credentials: mesh256,
                ...isDomainWeborNative
            },
            config: {
                API_URL_PREFIX,
                API_VERSION_PREFIX
            }
        }).then(redordingResponse => {
            const fallbackRecordingService = this.props._isDropboxEnabled
                ? RECORDING_TYPES.DROPBOX
                : this.props._isOneDriveEnabled
                    ? RECORDING_TYPES.ONEDRIVE
                    : this.state.selectedRecordingService;

            if (!redordingResponse || redordingResponse instanceof Error) {
                this.setState(prevState => {
                    return {
                        ...prevState,
                        selectedRecordingService: fallbackRecordingService,
                        isTokenValid: false,
                        isValidating: false,
                        isLoaded: false
                    };
                });

                return;
            }
            if (redordingResponse?.success === false) {
                const isDropboxAuthMissing = redordingResponse?.code === 1033;
                const isOneDriveAuthMissing = redordingResponse?.code === 1429;
                const isMeetHourStorageFull = redordingResponse?.code === 1032;
                const hasLocalDropboxToken = Boolean(this.props._token);
                const hasLocalOneDriveToken = Boolean(this.props._oneDriveToken);
                const canFallbackToLocalDropbox = isDropboxAuthMissing && hasLocalDropboxToken;
                const canFallbackToLocalOneDrive = isOneDriveAuthMissing && hasLocalOneDriveToken;
                const selectedServiceFromLocalFallback = canFallbackToLocalOneDrive
                    ? RECORDING_TYPES.ONEDRIVE
                    : canFallbackToLocalDropbox
                        ? RECORDING_TYPES.DROPBOX
                        : isMeetHourStorageFull
                            ? RECORDING_TYPES.MH_REC_SERVICE
                            : isDropboxAuthMissing
                                ? RECORDING_TYPES.DROPBOX
                                : isOneDriveAuthMissing
                                    ? RECORDING_TYPES.ONEDRIVE
                                    : fallbackRecordingService;
                const shouldUseLocalFallback = canFallbackToLocalDropbox || canFallbackToLocalOneDrive;

                // FIX: setState first, then dispatch in callback so componentDidUpdate
                // sees the correct selectedRecordingService before _onTokenUpdated runs.
                this.setState(prevState => {
                    return {
                        ...prevState,
                        dropLoaded: true,
                        isTokenValid: false,
                        isValidating: shouldUseLocalFallback,
                        userName: isDropboxAuthMissing ? undefined : prevState.userName,
                        spaceLeft: isDropboxAuthMissing ? undefined : prevState.spaceLeft,
                        oneDriveUserName: isOneDriveAuthMissing ? undefined : prevState.oneDriveUserName,
                        oneDriveSpaceLeft: isOneDriveAuthMissing ? undefined : prevState.oneDriveSpaceLeft,
                        oneDriveUserEmail: isOneDriveAuthMissing ? undefined : prevState.oneDriveUserEmail,
                        oneDriveSpaceTotal: isOneDriveAuthMissing ? undefined : prevState.oneDriveSpaceTotal,
                        oneDriveSpaceUsed: isOneDriveAuthMissing ? undefined : prevState.oneDriveSpaceUsed,
                        recTypeData: {
                            ...prevState.recTypeData,
                            storage: isMeetHourStorageFull
                                ? prevState.recTypeData?.storage || 'local'
                                : prevState.recTypeData?.storage,
                            s3_free_space: redordingResponse?.s3_free_space
                                ?? prevState.recTypeData?.s3_free_space
                                ?? null,
                            s3_used_space: redordingResponse?.s3_used_space
                                ?? prevState.recTypeData?.s3_used_space
                                ?? null,
                            code: redordingResponse?.code
                        },
                        isAws: isMeetHourStorageFull ? true : prevState.isAws,
                        selectedRecordingService: selectedServiceFromLocalFallback,
                        isLoaded: false
                    };
                }, () => {
                    // API is the source of truth, but if it returns no token,
                    // keep valid local auth and hydrate signed-in provider details.
                    if (shouldUseLocalFallback) {
                        this._onTokenUpdated();
                    }
                });
            }
            if (redordingResponse?.success === true) {
                const recData = redordingResponse;
                const settings = recData?.settings || {};
                const storageType = recData?.default_recording_storage;
                const dropboxTokenSource = settings?.dropbox_access_token;
                const oneDriveTokenSource = settings?.onedrive_access_token
                    || (storageType === 'OneDrive' ? settings : undefined);
                const dropboxToken = dropboxTokenSource?.access_token;
                const dropboxRToken = dropboxTokenSource?.refresh_token;
                const dropboxExpires = dropboxTokenSource?.expires || dropboxTokenSource?.expires_in;
                const oneDriveToken = oneDriveTokenSource?.access_token;
                const oneDriveRToken = oneDriveTokenSource?.refresh_token;
                const oneDriveExpires = oneDriveTokenSource?.expires || oneDriveTokenSource?.expires_in;
                const selectedRecordingService = isCustomS3RecordingStorage(storageType)
                    ? RECORDING_TYPES.CUSTOM_S3
                    : isLocalRecordingStorage(storageType)
                        ? RECORDING_TYPES.MH_REC_SERVICE
                        : storageType === 'OneDrive'
                            ? RECORDING_TYPES.ONEDRIVE
                            : storageType === 'Dropbox'
                                ? RECORDING_TYPES.DROPBOX
                                : this.state.selectedRecordingService;

                // Hydrate both providers from portal settings when available.
                if (dropboxToken) {
                    this._dropboxTokenFromRecordSettings = dropboxToken;
                    this.props.dispatch(updateDropboxToken(dropboxToken, dropboxRToken, dropboxExpires));
                }
                if (oneDriveToken) {
                    this._oneDriveTokenFromRecordSettings = oneDriveToken;
                    this.props.dispatch(updateOneDriveToken(oneDriveToken, oneDriveRToken, oneDriveExpires));
                }
                this.setState(prevState => {
                    return {
                        ...prevState,
                        recTypeData: { settings: recData.settings,
                            storage: storageType,
                            s3_free_space: recData?.s3_free_space ? recData?.s3_free_space : null,
                            s3_used_space: recData?.s3_used_space ? recData?.s3_used_space : null
                        },
                        selectedRecordingService,
                        dropLoaded: true,
                        isTokenValid: Boolean(dropboxToken || oneDriveToken),
                        isValidating: Boolean(dropboxToken || oneDriveToken),
                        isAws: Boolean(recData?.default_recording_storage !== 'Dropbox'
                            && recData?.default_recording_storage !== 'OneDrive'
                            && !_.isNil(recData?.default_recording_storage)),
                        isLoaded: false
                    };
                }, () => {
                    // Always call _onTokenUpdated from the setState callback so the
                    // correct selectedRecordingService is visible regardless of
                    // whether the Redux dispatch and setState batched together.
                    this._onTokenUpdated();
                });
            }
        });
    }

    /**
     * Validates the oauth access token.
     *
     * @inheritdoc
     * @returns {void}
     */
    componentDidUpdate(prevProps: Props) {
        const oneDriveAuthFinished = prevProps._oneDriveAuthorizing && !this.props._oneDriveAuthorizing;
        const oneDriveAuthStarted = !prevProps._oneDriveAuthorizing && this.props._oneDriveAuthorizing;

        // Show spinner immediately when Sign In is clicked.
        if (oneDriveAuthStarted) {
            this.setState({ isValidating: true,
                isTokenValid: false });
        }

        // Refresh config only when auth finished without a token (fallback)
        // or when a brand-new token appears from non-recordsettings flows.
        const shouldReloadConfigAfterAuth = oneDriveAuthFinished && !this.props._oneDriveToken;

        if (shouldReloadConfigAfterAuth) {
            this._fetchRecordingConfig();
        }

        // Clear cached recordsettings tokens when user explicitly signs out.
        if (prevProps._oneDriveToken && !this.props._oneDriveToken) {
            this._oneDriveTokenFromRecordSettings = null;
            this._lastSavedOneDriveToken = null;
            this._lastTokenValidationKey = null;
            resetProviderValidation(`${RECORDING_TYPES.ONEDRIVE}:`);
            resetProviderSavedTokens(`${RECORDING_TYPES.ONEDRIVE}:`);
        }

        if (prevProps._token && !this.props._token) {
            this._dropboxTokenFromRecordSettings = null;
            this._lastSavedDropboxToken = null;
            this._lastTokenValidationKey = null;
            resetProviderValidation(`${RECORDING_TYPES.DROPBOX}:`);
            resetProviderSavedTokens(`${RECORDING_TYPES.DROPBOX}:`);
        }

        if (this.props._token !== prevProps._token
            || this.props._oneDriveToken !== prevProps._oneDriveToken
            || this.props._accessToken !== prevProps._accessToken
            || oneDriveAuthFinished) {
            this._onTokenUpdated();
        }
    }

    _areIntegrationsEnabled: () => boolean;

    /**
     * Returns true if the integrations with third party services are enabled
     * and false otherwise.
     *
     * @returns {boolean} - True if the integrations with third party services
     * are enabled and false otherwise.
     */
    _areIntegrationsEnabled() {
        return this.props._isDropboxEnabled || this.props._isOneDriveEnabled;
    }

    _onSharingSettingChanged: () => void;

    /**
     * Callback to handle sharing setting change from the dialog.
     *
     * @returns {void}
     */
    _onSharingSettingChanged() {
        this.setState({
            sharingEnabled: !this.state.sharingEnabled
        });
    }

    _onSelectedRecordingServiceChanged: (string) => void;

    _isStorageFullForSelectedRecordingService: () => boolean;

    /**
     * Returns whether selected recording storage is full.
     *
     * @returns {boolean}
     */
    _isStorageFullForSelectedRecordingService() {
        const { selectedRecordingService, oneDriveSpaceLeft, spaceLeft, recTypeData, isAws } = this.state;
        const storageType = recTypeData?.storage;

        if (recTypeData?.code === 1032) {
            return true;
        }

        if (selectedRecordingService === RECORDING_TYPES.ONEDRIVE) {
            const oneDriveFreeSpace = parseStorageValue(oneDriveSpaceLeft);

            return oneDriveFreeSpace !== null && oneDriveFreeSpace <= 0;
        }

        if (selectedRecordingService === RECORDING_TYPES.DROPBOX) {
            const dropboxFreeSpace = parseStorageValue(spaceLeft);

            return dropboxFreeSpace !== null && dropboxFreeSpace <= 0;
        }

        if (isCustomS3RecordingStorage(storageType)) {
            const s3FreeSpace = parseStorageValue(recTypeData?.s3_free_space);

            return s3FreeSpace !== null && s3FreeSpace <= 0;
        }

        if (isAws || selectedRecordingService === RECORDING_TYPES.MH_REC_SERVICE) {
            const s3FreeSpace = parseStorageValue(recTypeData?.s3_free_space);

            return s3FreeSpace !== null && s3FreeSpace <= 0;
        }

        return false;
    }

    /**
     * Handles selected recording service changes.
     *
     * @param {string} selectedRecordingService - The new selected recording
     * service.
     * @returns {void}
     */
    _onSelectedRecordingServiceChanged(selectedRecordingService) {
        this.setState({ selectedRecordingService }, () => {
            this._onTokenUpdated();
        });
    }

    /**
     * Validates the provider access token and fetches account information.
     *
     * @returns {void}
     */
    _onTokenUpdated() {
        const {
            _appKey,
            _isDropboxEnabled,
            _isOneDriveEnabled,
            _token,
            _accessToken,
            _rToken,
            _tokenExpireDate,
            _oneDriveToken,
            _oneDriveRToken,
            _oneDriveTokenExpireDate,
            _oneDriveClientId,
            dispatch
        } = this.props;
        const resolvedDropboxToken = _token || this._dropboxTokenFromRecordSettings;
        const resolvedOneDriveToken = _oneDriveToken || this._oneDriveTokenFromRecordSettings;

        if (!_isDropboxEnabled && !_isOneDriveEnabled) {
            return;
        }

        // While OneDrive popup + server exchange is in flight, show spinner immediately.
        if (this.props._oneDriveAuthorizing) {
            this.setState({
                isTokenValid: false,
                isValidating: true
            });

            return;
        }

        if (!resolvedDropboxToken && !resolvedOneDriveToken) {
            this.setState({
                isTokenValid: false,
                isValidating: false
            });

            return;
        }

        const selectedRecordingService = this.state.selectedRecordingService;
        const isDropboxService = selectedRecordingService === RECORDING_TYPES.DROPBOX;
        const isOneDriveService = selectedRecordingService === RECORDING_TYPES.ONEDRIVE;

        // For non-integration services (MH_REC_SERVICE, Custom S3, etc.) no token
        // validation is needed — do not call provider Graph APIs for these services.
        if (!isDropboxService && !isOneDriveService) {
            return;
        }

        // If the selected provider's token is absent (signed out or server returned none),
        // show sign-in button immediately.
        const providerToken = isOneDriveService ? resolvedOneDriveToken : resolvedDropboxToken;

        if (!providerToken) {
            this.setState({ isTokenValid: false,
                isValidating: false });

            return;
        }

        const validationKey = `${selectedRecordingService}:${providerToken}`;

        if (!tryStartProviderValidation(validationKey)) {
            return;
        }

        this._inFlightTokenValidationKey = validationKey;

        const shouldRefreshDropboxToken = isDropboxService
            && Boolean(resolvedDropboxToken)
            && _tokenExpireDate
            && Date.now() > new Date(_tokenExpireDate)
            && checkIfNotReactNative() === true;

        if (shouldRefreshDropboxToken) {
            getNewDropboxAccessToken(_appKey, _rToken)
                .then(resp => dispatch(updateDropboxToken(resp.token, resp.rToken, resp.expireDate)))
                .catch(error => {
                    logger.log('Cannot refresh Dropbox token', error);
                    dispatch(showErrorNotification({
                        titleKey: 'recording.dropboxError',
                        descriptionKey: 'recording.dropboxRefreshTokenFailed'
                    }));
                    this.setState({
                        isTokenValid: false,
                        isValidating: false
                    });
                });

            return;
        }

        if (this.state.dropLoaded && _accessToken) {
            const { apiN: { API_URL_PREFIX, API_VERSION_PREFIX } } = this.props;
            const shouldSaveDropboxToken = isDropboxService
                && Boolean(resolvedDropboxToken)
                && resolvedDropboxToken !== this._dropboxTokenFromRecordSettings
                && resolvedDropboxToken !== this._lastSavedDropboxToken
                && tryMarkProviderTokenSaved(`${RECORDING_TYPES.DROPBOX}:${resolvedDropboxToken}`);
            const shouldSaveOneDriveToken = isOneDriveService
                && Boolean(resolvedOneDriveToken)
                && Boolean(_oneDriveRToken)
                && resolvedOneDriveToken !== this._oneDriveTokenFromRecordSettings
                && resolvedOneDriveToken !== this._lastSavedOneDriveToken
                && tryMarkProviderTokenSaved(`${RECORDING_TYPES.ONEDRIVE}:${resolvedOneDriveToken}`);

            if (shouldSaveDropboxToken) {
                this._lastSavedDropboxToken = resolvedDropboxToken;
                fetchAxiosApi('customer/savedropboxtoken', {
                    method: 'post',
                    headers: { 'Content-Type': 'application/json',
                        'Authorization': `Bearer ${_accessToken}`
                    },
                    body: {
                        meeting_id: this.props._roomName,
                        dropbox_token: resolvedDropboxToken,
                        refresh_token: _rToken,
                        expires: `${_tokenExpireDate}`
                    },
                    config: {
                        API_URL_PREFIX,
                        API_VERSION_PREFIX
                    }
                });
            }

            if (shouldSaveOneDriveToken) {
                this._lastSavedOneDriveToken = resolvedOneDriveToken;
                fetchAxiosApi('customer/saveonedrivetoken', {
                    method: 'post',
                    headers: { 'Content-Type': 'application/json',
                        'Authorization': `Bearer ${_accessToken}`
                    },
                    body: {
                        meeting_id: this.props._roomName,
                        onedrive_token: resolvedOneDriveToken,
                        refresh_token: _oneDriveRToken,
                        expires_in: `${_oneDriveTokenExpireDate}`
                    },
                    config: {
                        API_URL_PREFIX,
                        API_VERSION_PREFIX
                    }
                });
            }
            this.setState({
                isTokenValid: false,
                isValidating: true
            });
        }

        const shouldFetchDropboxData = isDropboxService && Boolean(resolvedDropboxToken);
        const shouldFetchOneDriveData = isOneDriveService && Boolean(resolvedOneDriveToken);

        if (!shouldFetchDropboxData && !shouldFetchOneDriveData) {
            return;
        }

        this.setState({
            isTokenValid: false,
            isValidating: true
        });

        if (shouldFetchDropboxData) {
            getDropboxData(resolvedDropboxToken, _appKey).then(data => {
                this._inFlightTokenValidationKey = null;
                this._lastTokenValidationKey = validationKey;
                markProviderValidationSuccess(validationKey);
                if (typeof data === 'undefined') {
                    this.setState({
                        isTokenValid: true,
                        isValidating: false
                    });
                } else {
                    this.setState({
                        isTokenValid: true,
                        isValidating: false,
                        ...data
                    });
                }
            })
            .catch(error => {
                this._inFlightTokenValidationKey = null;
                markProviderValidationFailed(validationKey);
                logger.log('getDropboxDataError', error);
                this.setState({ isTokenValid: false,
                    isValidating: false });
            });
        }

        if (shouldFetchOneDriveData) {
            getOneDriveData(resolvedOneDriveToken, _oneDriveClientId, {
                rToken: _oneDriveRToken,
                strategy: this.props._oneDriveAuthStrategy,
                onRefreshed: refreshed => {
                    dispatch(updateOneDriveToken(refreshed.token, refreshed.rToken, refreshed.expireDate));
                }
            }).then(data => {
                this._inFlightTokenValidationKey = null;
                this._lastTokenValidationKey = validationKey;
                markProviderValidationSuccess(validationKey);
                if (typeof data === 'undefined') {
                    this.setState({
                        isTokenValid: true,
                        isValidating: false
                    });
                } else {
                    this.setState({
                        isTokenValid: true,
                        isValidating: false,
                        oneDriveUserName: data.userName,
                        oneDriveSpaceLeft: data.spaceLeft,
                        oneDriveUserEmail: data.userEmail,
                        oneDriveSpaceTotal: data.spaceTotalMB,
                        oneDriveSpaceUsed: data.spaceUsedMB
                    });
                }
            })
            .catch(error => {
                this._inFlightTokenValidationKey = null;
                markProviderValidationFailed(validationKey);
                logger.log('getOneDriveDataError', error);
                this.setState({ isTokenValid: false,
                    isValidating: false });
            });
        }
    }

    _onSubmit: () => boolean;

    /**
     * Starts a file recording session.
     *
     * @private
     * @returns {boolean} - True (to note that the modal should be closed).
     */
    _onSubmit() {
        const { _autoCaptionOnRecord, _conference,
            _token, _oneDriveToken, dispatch, _accessToken,
            apiN: { API_URL_PREFIX, API_VERSION_PREFIX, MH_CLIENT_ID },
            _rToken, _oneDriveRToken, _oneDriveClientId, _appKey, _roomName,
            _confSettings, _localparticipant, _requestingSubtitles } = this.props;
        const storageType = this.state.recTypeData?.storage;
        const isLegacyCustomStorage = Boolean(storageType)
            && !isLocalRecordingStorage(storageType)
            && storageType !== 'Dropbox'
            && storageType !== 'OneDrive';
        const isCustomS3Recording = isCustomS3RecordingStorage(storageType)
            || isLegacyCustomStorage;

        if (this._isStorageFullForSelectedRecordingService()) {
            dispatch(showErrorNotification({
                titleKey: 'recording.storageFullTitle',
                descriptionKey: 'recording.storageFullDescription'
            }));

            return false;
        }

        let appData;
        const attributes = {};

        const isOneDriveRecording = this.state.selectedRecordingService === RECORDING_TYPES.ONEDRIVE
            && _oneDriveToken;
        const isDropboxRecording = this.state.selectedRecordingService === RECORDING_TYPES.DROPBOX
            && _token;
        const isMeetHourStorage = this.state.selectedRecordingService === RECORDING_TYPES.MH_REC_SERVICE;

        if (isOneDriveRecording) {
            const recordURL = `${API_URL_PREFIX}${API_VERSION_PREFIX}customer/saverecordings`;
            const decryptUrl = `${API_URL_PREFIX}${API_VERSION_PREFIX}customer/recordsettings_decrypt`;

            appData = JSON.stringify({
                'file_recording_metadata': {
                    'upload_credentials': {
                        'service_name': RECORDING_TYPES.ONEDRIVE,
                        'recordingType': 'OneDrive',
                        token: _oneDriveToken,
                        'userid': this.props._userId?.id,
                        _accessToken,
                        'r_token': _oneDriveRToken,
                        client_id: MH_CLIENT_ID,
                        onedrive_client_id: _oneDriveClientId,
                        'urlSaved': recordURL,
                        decryptUrl,
                        api_key: apiKEY,
                        ...isDomainWeborNative,
                        'startTiming': new Date().getTime(),
                        'meetingid': _roomName
                    }
                }
            });
            attributes.type = RECORDING_TYPES.ONEDRIVE;
        } else if (isDropboxRecording || isCustomS3Recording || isMeetHourStorage) {
            const serviceData = isCustomS3Recording || isMeetHourStorage
                ? this.state.recTypeData
                : { 'token': _token };
            const recordingType = isCustomS3Recording || isMeetHourStorage
                ? storageType || 'local'
                : 'Dropbox';
            const recordURL = `${API_URL_PREFIX}${API_VERSION_PREFIX}customer/saverecordings`;
            const decryptUrl = `${API_URL_PREFIX}${API_VERSION_PREFIX}customer/recordsettings_decrypt`;

            appData = JSON.stringify({
                'file_recording_metadata': {
                    'upload_credentials': {
                        // Keep legacy wire value so downstream Jibri/portal parsing remains compatible.
                        'service_name': isCustomS3Recording || isMeetHourStorage
                            ? 'awss3'
                            : RECORDING_TYPES.DROPBOX,
                        'recordingType': recordingType,
                        ...serviceData,
                        'userid': this.props._userId?.id,
                        _accessToken,
                        'r_token': _rToken,
                        'app_key': _appKey,
                        'urlSaved': recordURL,
                        decryptUrl,
                        client_id: MH_CLIENT_ID,
                        api_key: apiKEY,
                        ...isDomainWeborNative,
                        'startTiming': new Date().getTime(),
                        'meetingid': _roomName
                    }
                }
            });
            attributes.type = isCustomS3Recording
                ? RECORDING_TYPES.CUSTOM_S3
                : isMeetHourStorage
                    ? RECORDING_TYPES.MH_REC_SERVICE
                    : RECORDING_TYPES.DROPBOX;
        } else {
            appData = JSON.stringify({
                'file_recording_metadata': {
                    'share': this.state.sharingEnabled
                }
            });
            attributes.type = RECORDING_TYPES.MH_REC_SERVICE;
        }

        sendAnalytics(createRecordingDialogEvent('start', 'confirm.button', attributes));

        // Mark the local participant as the one who started the recording BEFORE calling
        // startRecording() so the flag is already set when the RECORDER_STATE_CHANGED XMPP
        // event arrives and middleware.js evaluates the consent dialog logic.
        // (The consent modal is exclusively managed in middleware.js on status === ON.)
        dispatch(recordingStartedByUser(_localparticipant?.id));

        startRecordingSafely(_conference, {
            mode: MHRecordingConstants.mode.FILE,
            appData
        });

        if (_confSettings?.AUTO_CAPTION_ON_RECORDING ?? _autoCaptionOnRecord) {
            if (_confSettings?.TRANSCRIPTION_QUOTA_EXCEEDED === 1) {
                dispatch(showErrorNotification({
                    descriptionKey: i18next.t('transcribing.transcriptionQuotaExceeded'),
                    titleKey: i18next.t('transcribing.transcriptionQuotaExceededTitle')
                }));
            } else if (_requestingSubtitles === true) {
                // no action
            } else {
                dispatch(toggleRequestingSubtitles());
            }
        }

        dispatch(hideDialog());

        return true;
    }

    /**
     * Renders the platform specific dialog content.
     *
     * @protected
     * @returns {React$Component}
     */
    _renderDialogContent: () => React$Component<*>;
}

/**
 * Maps (parts of) the Redux state to the associated props for the
 * {@code StartRecordingDialog} component.
 *
 * @param {Object} state - The Redux state.
 * @private
 * @returns {Object}
 */
export function mapStateToProps(state: Object) {
    const {
        transcription,
        fileRecordingsServiceEnabled = false,
        fileRecordingsServiceSharingEnabled = false,
        dropbox = {},
        onedrive = {}
    } = state['features/base/config'];
    const { _requestingSubtitles } = state['features/subtitles'];
    const { API_URL_PREFIX, API_VERSION_PREFIX, MH_CLIENT_ID } = state['features/base/config'];
    const apiN = {
        API_URL_PREFIX,
        API_VERSION_PREFIX,
        MH_CLIENT_ID
    };

    return {
        _appKey: dropbox.appKey,
        _autoCaptionOnRecord: transcription?.autoCaptionOnRecord ?? false,
        _conference: state['features/base/conference'].conference,
        _fileRecordingsServiceEnabled: fileRecordingsServiceEnabled,
        _fileRecordingsServiceSharingEnabled: fileRecordingsServiceSharingEnabled,
        _isDropboxEnabled: isDropboxEnabled(state),
        _isOneDriveEnabled: isOneDriveEnabled(state),
        _token: state['features/dropbox'].token,
        _oneDriveToken: state['features/onedrive'].token,
        _accessToken: state['features/base/jwt']?.accessToken,
        _userId: state['features/base/jwt']?.meetingDetails,
        _roomName: state['features/base/conference']?.room,
        _participants: state['features/base/participants'],
        apiN,
        _rToken: state['features/dropbox'].rToken,
        _oneDriveRToken: state['features/onedrive'].rToken,
        _tokenExpireDate: state['features/dropbox'].expireDate,
        _oneDriveTokenExpireDate: state['features/onedrive'].expireDate,
        _oneDriveClientId: onedrive.clientId,
        _oneDriveAuthStrategy: onedrive.authStrategy,
        _oneDriveAuthorizing: Boolean(state['features/onedrive']?.authorizing),
        _confSettings: state['features/base/jwt']?.confSettings,
        _localparticipant: getLocalParticipant(state['features/base/participants']),
        _requestingSubtitles
    };
}

export default AbstractStartRecordingDialog;
